import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  identityEntityCreateBusinessEntityMutation,
  identityEntityCreatePersonEntityMutation,
  identityEntityGetEntityOptions,
  identityEntityUpdateBusinessEntityMutation,
  identityEntityUpdatePersonEntityMutation,
} from '@column/api';
import { Button, Fade, Icon, OptionGroup } from '@column/column-ui-kit';

import { useBreadcrumbTitle } from '~/app/Layout/PageBreadcrumbs';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { CopyInput, Loading, RenderFields, RestrictedBanner } from '~/components';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNavigate } from '~/lib/navigation';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Hr, Inner } from '~/styles';
import { ApiError } from '~/typings/API';
import { ApiErrCode } from '~/typings/enum';

import { BusinessEntity } from './_partials/BusinessEntity';
import { PersonEntity } from './_partials/PersonEntity';
import {
  BusinessEntityFormValues,
  EntityFormType,
  PersonEntityFormValues,
  buildBusinessCreateRequest,
  buildBusinessUpdateRequest,
  buildPersonCreateRequest,
  buildPersonUpdateRequest,
  createDefaultPersonFormValues,
  createInitialBusinessFormState,
  getBusinessFormValuesFromEntity,
  getPersonFormValuesFromEntity,
  useEntityForm,
} from './_util/useEntityForm';

export { EntityFormType as EntityTypes };

interface Params {
  id: string;
  type: string;
}

export const PageEntitiesEdit: React.FC = () => {
  const { currentUser, isAuthorized } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { handleSubmitErrors } = useFormErrorHandler();
  const { id, type: typeParam } = useParams<keyof Params>() as Params;
  const type = (
    typeParam === EntityFormType.Person || typeParam === EntityFormType.Business ? typeParam : EntityFormType.Person
  ) as EntityFormType;
  const [displayType, setDisplayType] = useState<EntityFormType>(type);
  const previousTypeRef = useRef<EntityFormType>(type);

  const form = useEntityForm(displayType);

  const createPersonMutation = useMutation(identityEntityCreatePersonEntityMutation());
  const updatePersonMutation = useMutation(identityEntityUpdatePersonEntityMutation());
  const createBusinessMutation = useMutation(identityEntityCreateBusinessEntityMutation());
  const updateBusinessMutation = useMutation(identityEntityUpdateBusinessEntityMutation());

  const entityQueryOptions = identityEntityGetEntityOptions({ path: { entity_id: id ?? '' } });
  const {
    data: entityData,
    isLoading,
    error: entityError,
  } = useQuery({
    ...entityQueryOptions,
    enabled: !!id && !!currentUser,
    retry: (failureCount: number, error: unknown) => {
      const apiError = error as Partial<ApiError> | undefined;
      if (String(apiError?.code) === ApiErrCode.ResourceNotFound.toString()) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const entityName = id
    ? type === EntityFormType.Person
      ? [entityData?.person_details?.first_name, entityData?.person_details?.last_name].filter(Boolean).join(' ')
      : entityData?.business_details?.business_name
    : undefined;

  useBreadcrumbTitle(entityName);

  useEffect(() => {
    if (entityError) {
      navigate(ROUTE.ENTITIES);
      addDangerNotification({
        content: (entityError as Error).message ?? 'Failed to load entity',
      });
    }
  }, [entityError, navigate, addDangerNotification]);

  useEffect(() => {
    if (!entityData) {
      return;
    }

    if (type === EntityFormType.Person) {
      form.reset(getPersonFormValuesFromEntity(entityData));
    } else {
      form.reset(getBusinessFormValuesFromEntity(entityData));
    }
  }, [entityData, type, form]);

  useEffect(() => {
    if (type !== previousTypeRef.current) {
      previousTypeRef.current = type;
    } else if (displayType !== type && !id) {
      setDisplayType(type);
    }
  }, [type, id, displayType]);

  const handlePersonFormFadeOut = () => {
    if (!id && type === EntityFormType.Business && displayType === EntityFormType.Person) {
      form.reset(createInitialBusinessFormState());
      setDisplayType(EntityFormType.Business);
    }
  };

  const handleBusinessFormFadeOut = () => {
    if (!id && type === EntityFormType.Person && displayType === EntityFormType.Business) {
      form.reset(createDefaultPersonFormValues());
      setDisplayType(EntityFormType.Person);
    }
  };

  useEffect(() => {
    if (!id) {
      if (displayType === EntityFormType.Person) {
        form.reset(createDefaultPersonFormValues());
      } else {
        form.reset(createInitialBusinessFormState());
      }
    }
  }, [id, displayType, form]);

  const handleSubmit = form.handleSubmit(async () => {
    if (!currentUser) return;

    const values = form.getValues() as PersonEntityFormValues | BusinessEntityFormValues;

    try {
      if (id) {
        if (type === EntityFormType.Person) {
          const payload = buildPersonUpdateRequest(values as PersonEntityFormValues);
          const response = await updatePersonMutation.mutateAsync({ path: { entity_id: id }, body: payload });
          addSuccessNotification({
            content: `${response.person_details?.first_name} ${response.person_details?.last_name} updated`,
          });
        } else {
          const payload = buildBusinessUpdateRequest(values as BusinessEntityFormValues);
          const response = await updateBusinessMutation.mutateAsync({ path: { entity_id: id }, body: payload });
          addSuccessNotification({
            content: `${response.business_details?.business_name} updated`,
          });
        }
      } else if (type === EntityFormType.Person) {
        const payload = buildPersonCreateRequest(values as PersonEntityFormValues);
        const response = await createPersonMutation.mutateAsync({ body: payload });
        navigate(ROUTE.ENTITIES);
        addSuccessNotification({
          content: `${response.person_details?.first_name} ${response.person_details?.last_name} added`,
        });
      } else {
        const payload = buildBusinessCreateRequest(values as BusinessEntityFormValues);
        const response = await createBusinessMutation.mutateAsync({ body: payload });
        navigate(ROUTE.ENTITIES);
        addSuccessNotification({
          content: `${response.business_details?.business_name} added`,
        });
      }
    } catch (error: any) {
      addDangerNotification({ content: error?.message ?? 'Unable to save entity' });
    }
  }, handleSubmitErrors);

  return (
    <FormProvider {...form}>
      <PageActions>
        <Button
          onClick={handleSubmit}
          isDisabled={!isAuthorized({ permission: 'entities', permissionLevel: 'write' })}
          size="small"
          icon={!id && <Icon.Plus />}
        >
          {id ? 'Save Changes' : 'Create Entity'}
        </Button>
      </PageActions>
      <Loading isLoading={isLoading} fullHeight>
        {!isAuthorized({ permission: 'entities', permissionLevel: 'write' }) && (
          <Inner px="var(--page-margin)">
            <RestrictedBanner />
          </Inner>
        )}

        {RenderFields<Params>({
          sections: [
            {
              fields: [
                {
                  id: 'id',
                  label: 'ID',
                  hide: !id,
                  children: ({ value }) => <CopyInput value={(value as string) ?? ''} isMonospaced />,
                },
                {
                  id: 'type',
                  label: 'Entity Type',
                  children: () => (
                    <OptionGroup orientation={OptionGroup.Orientation.Horizontal}>
                      {[
                        { label: 'Person', value: EntityFormType.Person },
                        { label: 'Business', value: EntityFormType.Business },
                      ].map(({ label, value }) => (
                        <OptionGroup.Option
                          key={label}
                          isActive={type === value}
                          isDisabled={!!id}
                          onClick={() => !id && navigate(`${ROUTE.ENTITIES}/edit/${value}`)}
                        >
                          <OptionGroup.Option.Radio />
                          <OptionGroup.Option.Label>{label}</OptionGroup.Option.Label>
                        </OptionGroup.Option>
                      ))}
                    </OptionGroup>
                  ),
                },
              ],
            },
          ],
        })}
        <Hr />

        <Inner p={0} data-disabled={!isAuthorized({ permission: 'entities', permissionLevel: 'write' })}>
          <Fade
            show={displayType === EntityFormType.Person && (Boolean(id) || type === EntityFormType.Person)}
            onExited={handlePersonFormFadeOut}
          >
            {displayType === EntityFormType.Person && (
              <PersonEntity id={id} disableForm={!isAuthorized({ permission: 'entities', permissionLevel: 'write' })} />
            )}
          </Fade>
          <Fade
            show={displayType === EntityFormType.Business && (Boolean(id) || type === EntityFormType.Business)}
            onExited={handleBusinessFormFadeOut}
          >
            {displayType === EntityFormType.Business && (
              <BusinessEntity
                id={id}
                disableForm={!isAuthorized({ permission: 'entities', permissionLevel: 'write' })}
              />
            )}
          </Fade>
        </Inner>
        <Inner px="var(--page-margin)">
          <Flex justifyContent="space-between" alignItems="center">
            <Button onClick={() => navigate(ROUTE.ENTITIES)} variant="secondary" size="small">
              {id ? 'Back' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSubmit}
              isDisabled={!isAuthorized({ permission: 'entities', permissionLevel: 'write' })}
              size="small"
              icon={!id && <Icon.Plus />}
            >
              {id ? 'Save Changes' : 'Create Entity'}
            </Button>
          </Flex>
        </Inner>
      </Loading>
    </FormProvider>
  );
};
